import type { CareerPathContent, CareerModule } from '@/types/journey';

// ─── Shared thumbnail pool ────────────────────────────────────────────────────

const THUMBS = {
  stem:       'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&fit=crop',
  code:       'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800&fit=crop',
  business:   'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&fit=crop',
  finance:    'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&fit=crop',
  humanities: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=800&fit=crop',
  writing:    'https://images.unsplash.com/photo-1455390582262-044cdead277a?w=800&fit=crop',
};

// ─── Helper: build locked/unlocked modules ────────────────────────────────────

function mod(
  id: string,
  order: number,
  title: string,
  description: string,
  duration: string,
  content: string,
  thumb: string,
  locked: boolean,
  traitDeltas: { traitId: string; delta: number }[],
  xp = 40,
): CareerModule {
  return {
    id,
    order,
    title,
    description,
    duration,
    thumbnailUrl: thumb,
    isLocked: locked,
    content,
    xpReward: xp,
    traitDeltas,
    quiz: [
      {
        id: `${id}_q1`,
        question: `What is the key takeaway from "${title}"?`,
        options: [
          'Foundational knowledge and practical skills',
          'Only memorising facts',
          'Skipping the basics',
          'Avoiding collaboration',
        ],
        correctAnswer: 0,
      },
      {
        id: `${id}_q2`,
        question: 'How do professionals in this field typically keep their skills current?',
        options: [
          'Continuous learning and practice',
          'Reading one book per year',
          'Relying solely on formal degrees',
          'Watching TV documentaries',
        ],
        correctAnswer: 0,
      },
    ],
    resources: [
      { id: `${id}_r1`, type: 'pdf',        title: `${title} — Study Guide` },
      { id: `${id}_r2`, type: 'flashcards', title: `${title} — Key Terms` },
      { id: `${id}_r3`, type: 'notes',      title: 'My Notes' },
    ],
  };
}

// ─── STEM & Technology ────────────────────────────────────────────────────────

const STEM_MODULES: CareerModule[] = [
  mod('stem_m1', 1, 'What is STEM?', 'An introduction to Science, Technology, Engineering and Mathematics as a career landscape.', '10:30', `
## Welcome to STEM & Technology

STEM covers some of the fastest-growing, highest-impact careers in Nigeria and the world. From software engineers building fintech apps to biomedical scientists improving healthcare, STEM professionals are at the centre of the 21st-century economy.

**In this lesson you will:**
- Understand what STEM means and why it matters in Nigeria
- Explore the major branches: Science, Technology, Engineering, Mathematics
- See real career stories from Nigerian STEM professionals

The demand for STEM skills in Nigeria is growing at over 30% annually. With over 200 million people, Nigeria's tech, health, and engineering sectors need hundreds of thousands of skilled graduates every year.
  `.trim(), THUMBS.stem, false, [{ traitId: 'T02', delta: 2 }, { traitId: 'T07', delta: 1 }], 30),

  mod('stem_m2', 2, 'Core Subjects & Why They Matter', 'How secondary school subjects like Maths, Physics and Chemistry map directly to STEM careers.', '14:00', `
## Core Subjects for STEM

Every STEM career traces back to a foundation in secondary school subjects. Understanding the connection early helps you focus your energy.

| Subject | Careers it unlocks |
|---------|-------------------|
| Mathematics | Engineering, Data Science, Finance |
| Physics | Electrical/Mechanical Engineering, Astrophysics |
| Chemistry | Medicine, Chemical Engineering, Pharmacy |
| Biology | Medicine, Biotech, Agriculture |
| Computer Science | Software, AI, Cybersecurity |

**Pro tip:** Even if you lean toward one area, strong Maths opens doors across all of STEM.
  `.trim(), THUMBS.code, false, [{ traitId: 'T02', delta: 3 }, { traitId: 'T05', delta: 2 }], 40),

  mod('stem_m3', 3, 'Thinking Like an Engineer', 'Problem decomposition, systematic thinking and prototyping — the engineering mindset.', '12:45', `
## The Engineering Mindset

Engineers don't just fix things — they design systems that prevent problems in the first place. This lesson introduces the problem-solving framework used across all engineering disciplines.

**The 5-step engineering approach:**
1. **Define** the problem clearly
2. **Research** existing solutions
3. **Design** multiple possible approaches
4. **Build** a prototype
5. **Test and iterate**

This same framework applies whether you're building a bridge, a mobile app, or a water purification system.
  `.trim(), THUMBS.stem, false, [{ traitId: 'T02', delta: 3 }, { traitId: 'T06', delta: 2 }], 45),

  mod('stem_m4', 4, 'Nigeria\'s Tech Ecosystem', 'Lagos, Abuja and beyond — understanding the Nigerian startup scene and where STEM jobs live.', '11:20', `
## Nigeria's Tech Ecosystem

Nigeria is home to Africa's largest tech ecosystem, centred in Lagos's "Yabacon Valley." With over 3,000 active startups and unicorns like Flutterwave, Paystack and Andela, opportunities have never been greater.

**Key hubs:**
- **Lagos** — Fintech, e-commerce, media tech
- **Abuja** — GovTech, defence, energy
- **Port Harcourt** — Oil & gas tech, engineering firms
- **Enugu / Ibadan** — Emerging startup scenes

Even without relocating, remote work means a student in Kano can work for a company in San Francisco.
  `.trim(), THUMBS.code, true, [{ traitId: 'T07', delta: 2 }, { traitId: 'T17', delta: 1 }], 40),

  mod('stem_m5', 5, 'Your STEM Career Roadmap', 'From SS1 to your first job — a step-by-step pathway through education, certifications and internships.', '13:10', `
## Your STEM Career Roadmap

**Secondary School (Now → SS3)**
- Focus on Maths, Physics/Chemistry/Biology depending on your interest
- Join science clubs, participate in STEM competitions (e.g. Cowbell Maths, JETS)
- Start exploring free online resources: Khan Academy, Scratch, Code.org

**University**
- Engineering, Computer Science, Medicine, Pure Sciences
- Join student technical associations (NIEE, NCS, etc.)
- Pursue internships from Year 2

**Entry-Level (0–2 years)**
- Graduate trainee programmes at Dangote, MTN, Access Bank Tech, etc.
- Bootcamps and accelerators (Andela, Decagon, SheCode Africa)

**Mid-Career (3–7 years)**
- Specialise, lead teams, build your portfolio
  `.trim(), THUMBS.stem, true, [{ traitId: 'T02', delta: 2 }, { traitId: 'T09', delta: 3 }], 50),
];

// ─── Business & Finance ───────────────────────────────────────────────────────

const BUSINESS_MODULES: CareerModule[] = [
  mod('biz_m1', 1, 'What is Business & Finance?', 'From entrepreneurship to investment banking — the full landscape of commerce careers.', '9:50', `
## Business & Finance Careers

Business and finance professionals are the engine room of every organisation — public and private. They plan, allocate resources, manage risk and create value.

**Major areas:**
- **Accounting & Audit** — Tracking and verifying financial transactions
- **Banking & Investment** — Lending, wealth management, capital markets
- **Entrepreneurship** — Starting and scaling ventures
- **Marketing & Sales** — Creating demand and communicating value
- **Economics & Policy** — Analysing markets, advising governments

Nigeria's financial services sector is the second largest in sub-Saharan Africa and one of the highest-paying.
  `.trim(), THUMBS.business, false, [{ traitId: 'T02', delta: 1 }, { traitId: 'T17', delta: 2 }], 30),

  mod('biz_m2', 2, 'Accounting Fundamentals', 'Debits, credits, balance sheets and income statements explained for beginners.', '15:30', `
## Accounting: The Language of Business

Accounting is often called the "language of business" — it's how organisations communicate their financial health.

**The 3 core financial statements:**
1. **Balance Sheet** — What the business owns vs owes at a point in time
2. **Income Statement** — Revenue and expenses over a period
3. **Cash Flow Statement** — How cash actually moved in and out

**Key equation:**
> Assets = Liabilities + Equity

Every transaction in a business can be expressed through this equation. Mastering this is the foundation of any finance career.
  `.trim(), THUMBS.finance, false, [{ traitId: 'T02', delta: 3 }, { traitId: 'T04', delta: 2 }], 40),

  mod('biz_m3', 3, 'Entrepreneurship in Nigeria', 'How to identify opportunities, validate ideas and build a business in the Nigerian market.', '13:00', `
## Entrepreneurship: Building in Nigeria

Nigeria's young, growing population creates unique business opportunities. But the environment also has specific challenges — power, logistics, regulation — that smart entrepreneurs learn to navigate.

**The Lean Startup approach for Nigeria:**
1. Identify a real pain point (talk to 20 potential customers first)
2. Build the simplest possible solution (MVP)
3. Get 5 paying customers before raising money
4. Iterate based on feedback

**Nigerian success stories started small:**
- Jumia — started as a pizza delivery app
- Paystack — started solving online payments for small shops
- PiggyVest — started as a WhatsApp savings group
  `.trim(), THUMBS.business, false, [{ traitId: 'T07', delta: 3 }, { traitId: 'T17', delta: 2 }], 45),

  mod('biz_m4', 4, 'Banking & Capital Markets', 'Understanding how Nigerian banks work, the stock exchange and investment products.', '14:15', `
## Banking & Capital Markets

The Nigerian financial system has three tiers: commercial banks, development banks, and capital markets. Each offers different career paths.

**Commercial Banking careers:**
- Relationship Manager, Credit Analyst, Treasury Officer, Risk Manager

**Capital Markets (Nigerian Exchange Group — NGX):**
- Stockbroker, Investment Analyst, Fund Manager, Compliance Officer

**Entry point:**
Most major banks run NYSC-tied graduate trainee programmes. Strong numerical skills and the ICAN/ACCA professional exam are your fastest path to advancement.
  `.trim(), THUMBS.finance, true, [{ traitId: 'T02', delta: 2 }, { traitId: 'T04', delta: 3 }], 40),

  mod('biz_m5', 5, 'Your Business Career Roadmap', 'From SS1 to CFO — building the qualifications, experience and network for a business career.', '12:00', `
## Your Business Career Roadmap

**Secondary School**
- Focus on Commerce, Economics, Accounts, Maths
- Join Junior Achievement Nigeria (JAN) business clubs
- Practice with simple business simulations

**University**
- Accounting, Finance, Economics, Business Admin, Law
- Get involved in student entrepreneurship clubs (ENACTUS, JCI)

**Professional Certifications (key differentiators):**
- ICAN / ACCA (Accounting)
- CFA (Investment)
- CIPM (People Management)
- ACA (Legal/Corporate)

**Career accelerators:**
- Big 4 accounting firms (Deloitte, KPMG, EY, PwC) — structured graduate schemes
- GTBank, Zenith, Access — well-known graduate trainee pipelines
  `.trim(), THUMBS.business, true, [{ traitId: 'T09', delta: 3 }, { traitId: 'T17', delta: 2 }], 50),
];

// ─── Humanities & Social Sciences ─────────────────────────────────────────────

const HUMANITIES_MODULES: CareerModule[] = [
  mod('hum_m1', 1, 'What are Humanities & Social Sciences?', 'Literature, history, psychology, sociology, law and communication — understanding human society.', '10:00', `
## Humanities & Social Sciences

These fields study the most complex system in the universe: human society. From understanding why wars happen, to designing cities people want to live in, to helping individuals through mental health challenges — humanities and social science careers shape everything around us.

**Core disciplines:**
- Literature & Linguistics
- History & Political Science
- Psychology & Counselling
- Sociology & Anthropology
- Law & Governance
- Communication & Journalism
- Philosophy & Religious Studies
  `.trim(), THUMBS.humanities, false, [{ traitId: 'T14', delta: 2 }, { traitId: 'T17', delta: 2 }], 30),

  mod('hum_m2', 2, 'Communication & Media Careers', 'Journalism, PR, digital media, content creation and broadcasting in Nigeria.', '12:30', `
## Communication & Media in Nigeria

Nigeria has one of Africa's most vibrant media landscapes — from traditional TV and radio to a booming digital creator economy.

**Career paths:**
- **Journalism** — News reporting, investigative journalism, editorial
- **PR & Communications** — Corporate communications, crisis management
- **Digital Media** — Content creation, social media management, SEO
- **Broadcasting** — TV presenting, radio hosting, podcast production

**Nigerian media giants hiring:**
Channels TV, NTA, Arise News, TechCabal, BusinessDay, Premium Times, Cool FM

**The creator economy:**
Nigerian YouTubers, TikTokers and newsletter writers are building significant income streams. A strong personal brand in your chosen niche can open doors faster than a traditional job application.
  `.trim(), THUMBS.writing, false, [{ traitId: 'T17', delta: 3 }, { traitId: 'T22', delta: 2 }], 40),

  mod('hum_m3', 3, 'Law & Governance', 'How the Nigerian legal system works and what a law career looks like from LASU to the bar.', '13:45', `
## Law & Governance in Nigeria

Law is one of Nigeria's most respected professions. The path is long but the career options are broad — from corporate law to public interest advocacy.

**The pathway:**
1. 5-year LLB degree at an accredited Nigerian university
2. Nigerian Law School (1 year) → Call to Bar ceremony
3. NYSC
4. Pupillage / chambers attachment
5. Independent practice or in-house counsel roles

**Specialisations in demand:**
- Corporate / M&A Law (highest paid)
- Oil & Gas Law
- Technology Law (fastest growing)
- Human Rights Law
- Real Estate Law
  `.trim(), THUMBS.humanities, false, [{ traitId: 'T02', delta: 2 }, { traitId: 'T17', delta: 2 }], 45),

  mod('hum_m4', 4, 'Psychology & Counselling', 'Mental health is a growing need in Nigeria — careers in clinical, educational and organisational psychology.', '11:50', `
## Psychology & Counselling Careers

Mental health awareness is rising rapidly in Nigeria. The World Health Organisation estimates that 1 in 4 Nigerians will experience a mental health condition — but there are fewer than 500 psychiatrists for 200 million people.

**Career paths:**
- **Clinical Psychologist** — Assessment and therapy in hospitals or private practice
- **Counselling Psychologist** — Schools, NGOs, community centres
- **Organisational Psychologist** — HR departments, consulting firms
- **Educational Psychologist** — Schools, learning support

**Qualification path:**
BSc Psychology → MSc / PGD Clinical Psychology → Licensed practice with NCP or APN
  `.trim(), THUMBS.humanities, true, [{ traitId: 'T14', delta: 3 }, { traitId: 'T23', delta: 3 }], 40),

  mod('hum_m5', 5, 'Your Humanities Career Roadmap', 'From SS1 to professional practice — building your path in humanities and social sciences.', '12:20', `
## Your Humanities Career Roadmap

**Secondary School**
- Excel in English Language, Literature, Government, Economics, CRS/IRS
- Develop your writing — start a blog, write for your school magazine
- Join debate clubs, model UN, public speaking competitions

**University**
- Mass Comm, Law, Psychology, Sociology, English, Political Science, History, Philosophy
- Internships at media houses, NGOs, law firms and government agencies

**Key differentiators:**
- Strong written and spoken English
- A published portfolio (articles, reports, creative work)
- Digital skills — social media, analytics, basic design

**Employers:**
NGOs (UNICEF, ActionAid, FATE Foundation), Media (NTA, Channels, TechCabal), Government, Law Firms, Consulting
  `.trim(), THUMBS.writing, true, [{ traitId: 'T09', delta: 2 }, { traitId: 'T17', delta: 3 }], 50),
];

// ─── Content map ──────────────────────────────────────────────────────────────

const CAREER_PATH_CONTENT_MAP: Record<string, CareerPathContent> = {
  broad_stem_tech: {
    pathId: 'broad_stem_tech',
    level: 'broad',
    name: 'STEM & Technology',
    description: 'Engineering, computing, mathematics and pure sciences — building and understanding the technical world.',
    imageUrl: THUMBS.stem,
    category: 'Technology',
    modules: STEM_MODULES,
    salaryData: [
      { level: 'Entry-Level',  salary: '₦80,000 – ₦200,000',   experience: '0–2 years' },
      { level: 'Mid-Level',    salary: '₦250,000 – ₦600,000',  experience: '3–7 years' },
      { level: 'Senior-Level', salary: '₦700,000 – ₦2,500,000+', experience: '8+ years' },
    ],
    foundationData: {
      goals: [
        'Build strong Maths and Physics foundations by SS3',
        'Complete at least one coding or electronics project',
        'Enter a national STEM competition (JETS, Cowbell Maths)',
        'Shadow or interview one STEM professional in your area of interest',
      ],
      activities: [
        'Join or start a STEM/robotics club at school',
        'Use free online platforms: Khan Academy, Scratch, Arduino',
        'Follow Nigerian tech newsletters: TechCabal, Techpoint.Africa',
        'Attend a hackathon or coding bootcamp holiday programme',
      ],
      milestones: [
        'Score 70%+ in Maths and one Science subject by JSS3',
        'Build a working prototype of any project (hardware or software)',
        'Earn one free online certificate (Coursera, edX, Cisco Networking Academy)',
        'Complete one internship or job-shadow placement',
      ],
    },
    industryInsights: {
      marketAnalysis: {
        title: 'Job Market Analysis',
        content: "Nigeria's tech sector employs over 2 million people and is growing at 30% annually. Fintech, healthtech and agritech are the hottest verticals, driven by a young population, rising smartphone penetration and the shift to digital payments post-2020.",
      },
      marketTrends: {
        title: 'Market Trends',
        content: 'AI and machine learning skills are commanding 40–60% salary premiums over general software engineering. Cloud computing (AWS, Azure, GCP) certifications are increasingly required by Nigerian enterprise employers. Blockchain is growing through fintech and identity verification applications.',
      },
      nigeriaOpportunities: {
        title: 'Nigeria-Specific Opportunities',
        content: "NITDA's technology incubators in six geopolitical zones offer funding and mentorship. Federal government's digital economy policy is creating thousands of GovTech roles. Over 600 active tech startups are hiring, with Andela, Decagon and Semicolon offering paid training-to-job pipelines.",
      },
      futureTrends: {
        title: 'Future Trends & Challenges',
        content: 'Remote work is here to stay — Nigerian engineers now regularly earn USD salaries while based locally. The challenge is infrastructure: reliable power, broadband and laptop access remain barriers outside major cities. Upskilling in soft skills (communication, project management) differentiates technical candidates.',
      },
    },
    certifications: [
      { title: 'Google IT Support Certificate', duration: '3–6 months', level: 'Beginner', description: 'Entry point into tech support and systems administration' },
      { title: 'AWS Cloud Practitioner', duration: '1–3 months', level: 'Beginner', description: 'Foundation cloud computing certification from Amazon' },
      { title: 'Cisco CCNA', duration: '3–6 months', level: 'Intermediate', description: 'Industry-standard networking certification' },
      { title: 'Microsoft Azure Developer Associate', duration: '3–6 months', level: 'Intermediate', description: 'Cloud development on Microsoft Azure platform' },
    ],
    requiredTraits: [
      { traitId: 'T02', threshold: 55 },
      { traitId: 'T05', threshold: 45 },
      { traitId: 'T06', threshold: 50 },
    ],
  },

  broad_business_finance: {
    pathId: 'broad_business_finance',
    level: 'broad',
    name: 'Business & Finance',
    description: 'Accounting, banking, marketing and entrepreneurship — creating and managing economic value.',
    imageUrl: THUMBS.business,
    category: 'Business',
    modules: BUSINESS_MODULES,
    salaryData: [
      { level: 'Entry-Level',  salary: '₦70,000 – ₦180,000',   experience: '0–2 years' },
      { level: 'Mid-Level',    salary: '₦200,000 – ₦500,000',  experience: '3–7 years' },
      { level: 'Senior-Level', salary: '₦600,000 – ₦3,000,000+', experience: '8+ years' },
    ],
    foundationData: {
      goals: [
        'Master double-entry bookkeeping before leaving SS3',
        'Start and run a small school-based business (even ₦1,000 capital)',
        'Read one business biography (e.g. Alhaji Aliko Dangote, Mo Ibrahim)',
        'Complete a Junior Achievement Nigeria business programme',
      ],
      activities: [
        'Manage a small savings/investment group with friends',
        "Track your school tuck shop's daily revenue and costs for a month",
        'Follow markets: NSE All-Share Index, USD/NGN exchange rate',
        'Attend a business seminar or entrepreneurship competition',
      ],
      milestones: [
        'Score 70%+ in Accounts/Economics and Maths',
        'Produce a simple business plan for any product or service',
        'Open a savings account and set a monthly savings target',
        'Complete one work experience placement at a business or bank',
      ],
    },
    industryInsights: {
      marketAnalysis: {
        title: 'Job Market Analysis',
        content: "Financial services is Nigeria's second-largest sector by contribution to GDP. With over 20 commercial banks, 100+ microfinance institutions and a rapidly growing fintech ecosystem, demand for finance professionals remains consistently high even in economic downturns.",
      },
      marketTrends: {
        title: 'Market Trends',
        content: 'Digital banking and embedded finance are creating new roles in product management, data analysis and compliance. ESG (Environmental, Social, Governance) investing is bringing sustainability roles into finance. Fintech companies like OPay, Moniepoint and PalmPay are hiring aggressively and paying competitively.',
      },
      nigeriaOpportunities: {
        title: 'Nigeria-Specific Opportunities',
        content: "The CBN's cashless policy and financial inclusion agenda require thousands of mobile money agents, compliance officers and digital product managers annually. Agriculture finance, SME lending and housing finance are dramatically underserved — creating first-mover advantage for those who specialise.",
      },
      futureTrends: {
        title: 'Future Trends & Challenges',
        content: 'Automation is replacing routine bookkeeping and audit tasks, elevating demand for analytical and advisory skills. Professionals who combine finance knowledge with data analysis (Excel → Python → SQL) are commanding 30–50% salary premiums. The ICAN and ACCA qualifications remain the gold standard for career advancement.',
      },
    },
    certifications: [
      { title: 'ICAN Foundation Level', duration: '6–12 months', level: 'Beginner', description: "Nigeria's premier accounting qualification — foundation level" },
      { title: 'ACCA Applied Knowledge', duration: '6–12 months', level: 'Beginner', description: 'International accounting qualification recognised worldwide' },
      { title: 'CFA Level 1', duration: '6–12 months', level: 'Intermediate', description: 'Gold standard investment analysis certification' },
      { title: 'Certified Financial Planner (CFP)', duration: '12–18 months', level: 'Expert', description: 'Personal financial planning and wealth management' },
    ],
    requiredTraits: [
      { traitId: 'T02', threshold: 50 },
      { traitId: 'T04', threshold: 45 },
      { traitId: 'T17', threshold: 45 },
    ],
  },

  broad_humanities_social: {
    pathId: 'broad_humanities_social',
    level: 'broad',
    name: 'Humanities & Social Sciences',
    description: 'Communication, psychology, sociology and history — understanding people and the societies they build.',
    imageUrl: THUMBS.humanities,
    category: 'Humanities',
    modules: HUMANITIES_MODULES,
    salaryData: [
      { level: 'Entry-Level',  salary: '₦60,000 – ₦150,000',   experience: '0–2 years' },
      { level: 'Mid-Level',    salary: '₦180,000 – ₦450,000',  experience: '3–7 years' },
      { level: 'Senior-Level', salary: '₦500,000 – ₦2,000,000+', experience: '8+ years' },
    ],
    foundationData: {
      goals: [
        'Read at least 10 books across fiction and non-fiction this year',
        'Write regularly — journal, blog or school newspaper',
        'Study a social issue affecting your community and propose a solution',
        'Shadow a professional in law, media or social work',
      ],
      activities: [
        'Join your school debate team or public speaking club',
        'Volunteer with a community or NGO organisation',
        'Start a reading club or book exchange with classmates',
        'Follow quality news sources daily: Premium Times, The Punch, BBC Africa',
      ],
      milestones: [
        'Score 70%+ in English Language, Literature or Government',
        'Write and publish at least one article or creative piece',
        'Deliver a prepared speech or presentation to an audience',
        'Complete a community service project with documented impact',
      ],
    },
    industryInsights: {
      marketAnalysis: {
        title: 'Job Market Analysis',
        content: "Nigeria's media, NGO and legal sectors employ hundreds of thousands of professionals. The NGO sector alone — international and local — offers thousands of entry-level roles in communications, programme management and research. Law remains one of the highest-earning professions for senior practitioners.",
      },
      marketTrends: {
        title: 'Market Trends',
        content: 'Digital content creation is the fastest-growing career path within humanities. Nigerian creators are building globally recognised brands on YouTube, TikTok and newsletters. Mental health is the fastest-growing employment area in social sciences, with telehealth platforms like Mentally Aware Nigeria Initiative expanding rapidly.',
      },
      nigeriaOpportunities: {
        title: 'Nigeria-Specific Opportunities',
        content: "Nigeria's massive Nollywood industry is the world's second-largest film producer and employs writers, directors, publicists, lawyers and production managers. The federal and 36 state governments are among the largest employers of social science graduates through public administration and civil service roles.",
      },
      futureTrends: {
        title: 'Future Trends & Challenges',
        content: 'AI is automating basic writing and research tasks, making critical thinking, creativity and emotional intelligence — the core humanities skills — more valuable than ever. Humanities graduates who add data literacy (surveys, qualitative analysis tools, basic statistics) consistently outperform peers in competitive hiring.',
      },
    },
    certifications: [
      { title: 'Google Digital Marketing Certificate', duration: '3–6 months', level: 'Beginner', description: 'Foundation digital marketing skills for communications roles' },
      { title: 'CIPR Certificate in PR', duration: '6 months', level: 'Intermediate', description: 'Professional public relations qualification' },
      { title: 'Nigerian Law School Bar', duration: '1 year', level: 'Expert', description: 'Required qualification to practise law in Nigeria' },
      { title: 'APA-Accredited Psychology Training', duration: '2–4 years', level: 'Expert', description: 'Postgraduate clinical or counselling psychology training' },
    ],
    requiredTraits: [
      { traitId: 'T14', threshold: 45 },
      { traitId: 'T17', threshold: 55 },
      { traitId: 'T22', threshold: 40 },
    ],
  },
};

// ─── Fallback generator for any path not in the map ──────────────────────────

function generateFallbackContent(pathId: string): CareerPathContent {
  const name = pathId
    .replace('broad_', '')
    .replace('field_', '')
    .replace('spec_', '')
    .replace(/_/g, ' & ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

  const level = pathId.startsWith('broad_') ? 'broad'
    : pathId.startsWith('field_') ? 'field'
    : 'specialization';

  const fallbackModules: CareerModule[] = [1, 2, 3, 4].map((n) =>
    mod(
      `${pathId}_m${n}`,
      n,
      `${name} — Module ${n}`,
      `Explore the key concepts and skills that make up ${name}.`,
      '10:00',
      `## ${name} — Module ${n}\n\nThis module explores the foundational concepts of ${name}. As this path continues to grow, detailed content will be added here.\n\nKey areas covered:\n- Core principles\n- Practical applications in the Nigerian context\n- Career pathways and opportunities\n- Real-world case studies`,
      THUMBS.stem,
      n > 2,
      [{ traitId: 'T02', delta: 2 }],
      35,
    )
  );

  return {
    pathId,
    level,
    name,
    description: `Explore careers and opportunities in ${name} — a growing field with strong prospects in Nigeria.`,
    imageUrl: THUMBS.stem,
    category: name,
    modules: fallbackModules,
    salaryData: [
      { level: 'Entry-Level',  salary: '₦70,000 – ₦180,000',  experience: '0–2 years' },
      { level: 'Mid-Level',    salary: '₦200,000 – ₦500,000', experience: '3–7 years' },
      { level: 'Senior-Level', salary: '₦600,000+',           experience: '8+ years' },
    ],
    foundationData: {
      goals: [
        `Build a strong academic foundation relevant to ${name}`,
        'Research professionals working in this field in Nigeria',
        'Identify the key university courses that feed into this career',
      ],
      activities: [
        'Join a relevant club or professional association',
        'Read industry news and publications',
        'Seek out a mentor or job-shadow opportunity',
      ],
      milestones: [
        'Complete the relevant secondary school subjects with strong grades',
        'Gain at least one work experience placement',
        'Earn a relevant entry-level certification',
      ],
    },
    industryInsights: {
      marketAnalysis: { title: 'Job Market Analysis', content: `${name} is a growing sector in Nigeria with increasing demand for skilled professionals across entry, mid and senior levels.` },
      marketTrends: { title: 'Market Trends', content: `Digital transformation and Nigeria's economic growth are creating new opportunities within ${name}.` },
      nigeriaOpportunities: { title: 'Nigeria-Specific Opportunities', content: `Nigeria's large, young population and expanding economy create significant demand for ${name} professionals.` },
      futureTrends: { title: 'Future Trends & Challenges', content: `Continuous learning and adaptability will be key to long-term success in ${name}.` },
    },
    certifications: [
      { title: `${name} Foundation Certificate`, duration: '3–6 months', level: 'Beginner', description: `Entry-level qualification for ${name}` },
      { title: `${name} Professional Diploma`, duration: '6–12 months', level: 'Intermediate', description: `Intermediate qualification for ${name}` },
    ],
    requiredTraits: [{ traitId: 'T02', threshold: 45 }],
  };
}

// ─── Public API ───────────────────────────────────────────────────────────────

/** Returns demo CareerPathContent for a given pathId.
 *  Falls back to a generated placeholder for unknown path IDs. */
export function getDemoPathContent(pathId: string): CareerPathContent {
  return CAREER_PATH_CONTENT_MAP[pathId] ?? generateFallbackContent(pathId);
}

/** Returns all broad-level demo paths (for listing pages). */
export function getAllDemoPaths(): CareerPathContent[] {
  return Object.values(CAREER_PATH_CONTENT_MAP);
}
